import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { format, isToday, isYesterday, isThisWeek, isThisYear } from 'date-fns';
import { useConfirmDialog } from '../../../ConfirmDialog';
import PanelHeading from '../../../PanelHeading';
import IconButton from '../../../IconButton';
import { TrashIcon } from '../../../IconButton/icons';
import './thread-panel.scss';

// =======================================
// INTERFACES
// =======================================

interface ThreadMessage {
  id: number;
  message: string;
  created_at: string;
  sender: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

interface ThreadPanelProps {
  projectId: number | null;
  stageId: number | null;
  onMessageChange?: () => void; // Called when a message is sent or deleted
}

interface MessageGroup {
  dateLabel: string;
  dateKey: string;
  messages: ThreadMessage[];
}

// =======================================
// HELPER FUNCTIONS
// =======================================

// Get user initials from name
const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
};

// Format date for separator labels
const formatDateSeparator = (dateString: string): string => {
  const date = new Date(dateString);
  
  if (isToday(date)) {
    return 'Today';
  }
  
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  
  if (isThisWeek(date)) {
    return format(date, 'EEEE'); // "Monday", "Tuesday", etc.
  }
  
  if (isThisYear(date)) {
    return format(date, 'MMMM d'); // "January 5"
  }
  
  return format(date, 'MMMM d, yyyy'); // "January 5, 2025"
};

// Get date key for grouping (YYYY-MM-DD)
const getDateKey = (dateString: string): string => {
  return format(new Date(dateString), 'yyyy-MM-dd');
};

// Format time for individual messages
const formatMessageTime = (dateString: string): string => {
  return format(new Date(dateString), 'h:mm a');
};

// =======================================
// MAIN COMPONENT
// =======================================

const ThreadPanel: React.FC<ThreadPanelProps> = ({ projectId, stageId, onMessageChange }) => {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<number | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentUserTeamMemberId, setCurrentUserTeamMemberId] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const supabase = useSupabaseClient();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const session = useSession();

  // Group messages by date
  const messageGroups = useMemo((): MessageGroup[] => {
    const groups: MessageGroup[] = [];
    let currentDateKey = '';
    let currentGroup: MessageGroup | null = null;

    messages.forEach((msg) => {
      const dateKey = getDateKey(msg.created_at);
      
      if (dateKey !== currentDateKey) {
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentDateKey = dateKey;
        currentGroup = {
          dateLabel: formatDateSeparator(msg.created_at),
          dateKey,
          messages: [msg]
        };
      } else if (currentGroup) {
        currentGroup.messages.push(msg);
      }
    });

    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups;
  }, [messages]);

  // Auto-resize textarea based on content - only grow when text wraps
  const autoResizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const minHeight = 36; // Initial single-line height
    const maxHeight = 120;
    
    // If empty, just reset to minimum
    if (!textarea.value.trim()) {
      textarea.style.height = `${minHeight}px`;
      textarea.style.overflowY = 'hidden';
      return;
    }
    
    // Temporarily set height to min to get accurate scrollHeight
    textarea.style.height = `${minHeight}px`;
    
    // Only grow if content exceeds single line
    if (textarea.scrollHeight > minHeight) {
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      // Show scrollbar only when at max height
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }, []);

  // Reset textarea height
  const resetTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '36px'; // Reset to initial height
      textarea.style.overflowY = 'hidden';
    }
  }, []);

  // Auto-resize when message content changes (catches all changes including programmatic)
  useEffect(() => {
    autoResizeTextarea();
  }, [newMessage, autoResizeTextarea]);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Fetch current user's team_member_id
  const fetchCurrentUserTeamMemberId = useCallback(async () => {
    if (!session?.user?.id) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('team_member_id')
        .eq('id', session.user.id)
        .single();

      if (fetchError) {
        console.error('Error fetching user profile:', fetchError);
        return;
      }

      setCurrentUserTeamMemberId(data?.team_member_id || null);
    } catch (err) {
      console.error('Error fetching user team member ID:', err);
    }
  }, [supabase, session?.user?.id]);

  // Fetch messages for this project or stage
  const fetchMessages = useCallback(async () => {
    // Determine which entity we're fetching messages for
    const entityId = stageId || projectId;
    const entityType = stageId ? 'stage_id' : 'project_id';

    if (!entityId) {
      setMessages([]);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('project_thread_messages')
        .select(`
          id,
          message,
          created_at,
          sender:team_members!thread_sender_fkey (
            id,
            first_name,
            last_name
          )
        `)
        .eq(entityType, entityId)
        .order('created_at', { ascending: true });

      if (fetchError) {
        console.error('Error fetching thread messages:', fetchError);
        setError('Failed to load messages');
        setMessages([]);
        return;
      }

      // Transform the data to match our interface
      const transformedMessages: ThreadMessage[] = (data || []).map((msg: any) => ({
        id: msg.id,
        message: msg.message,
        created_at: msg.created_at,
        sender: msg.sender
      }));

      setMessages(transformedMessages);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }, [supabase, projectId, stageId]);

  // Send a new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentUserTeamMemberId) return;

    const entityId = stageId || projectId;
    if (!entityId) return;

    setIsSending(true);
    setError(null);

    try {
      const messageData: {
        message: string;
        sender_id: string;
        project_id?: number;
        stage_id?: number;
      } = {
        message: newMessage.trim(),
        sender_id: currentUserTeamMemberId
      };

      // Set the appropriate entity ID
      if (stageId) {
        messageData.stage_id = stageId;
      } else if (projectId) {
        messageData.project_id = projectId;
      }

      const { error: insertError } = await supabase
        .from('project_thread_messages')
        .insert(messageData);

      if (insertError) {
        console.error('Error sending message:', insertError);
        setError('Failed to send message');
        return;
      }

      // Clear input and refresh messages
      setNewMessage('');
      resetTextareaHeight();
      await fetchMessages();
      
      // Notify parent that a message was sent (to update workflow cards)
      onMessageChange?.();
      
      // Scroll to bottom after new message
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Handle Enter key to send message
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Delete a message
  const handleDeleteMessage = async (messageId: number) => {
    const ok = await confirm({
      title: 'Delete this note?',
      message: 'It will be removed from the activity thread for everyone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    setDeletingMessageId(messageId);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('project_thread_messages')
        .delete()
        .eq('id', messageId);

      if (deleteError) {
        console.error('Error deleting message:', deleteError);
        setError('Failed to delete message');
        return;
      }

      // Remove message from local state
      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
      
      // Notify parent that a message was deleted (to update workflow cards)
      onMessageChange?.();
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    } finally {
      setDeletingMessageId(null);
    }
  };

  // Fetch user team member ID on mount
  useEffect(() => {
    fetchCurrentUserTeamMemberId();
  }, [fetchCurrentUserTeamMemberId]);

  // Fetch messages when projectId or stageId changes
  useEffect(() => {
    setIsLoading(true);
    fetchMessages();
  }, [fetchMessages]);

  // Scroll to bottom when messages load
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      scrollToBottom();
    }
  }, [isLoading, messages.length, scrollToBottom]);

  return (
    <div className="thread-panel">
      <div className="thread-panel__header">
        <PanelHeading
          as="h3"
          title="Activity"
          meta={!isLoading && messages.length > 0 ? `${messages.length} ${messages.length === 1 ? 'note' : 'notes'}` : undefined}
        />
      </div>

      <div className="thread-panel__messages">
        {isLoading ? (
          <div className="thread-panel__loading">
            <div className="message-skeleton" />
            <div className="message-skeleton" />
            <div className="message-skeleton" />
          </div>
        ) : messages.length === 0 ? (
          <div className="thread-panel__empty">
            <div className="thread-panel__empty-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p>No notes yet</p>
            <span>Anything the team should know about this job goes here.</span>
          </div>
        ) : (
          <>
            {messageGroups.map((group) => (
              <div key={group.dateKey} className="thread-panel__date-group">
                <div className="thread-panel__date-separator">
                  <span>{group.dateLabel}</span>
                </div>

                {group.messages.map((msg) => {
                  const isOwnMessage = msg.sender?.id === currentUserTeamMemberId;
                  const isDeleting = deletingMessageId === msg.id;
                  const senderName = `${msg.sender?.first_name ?? ''} ${msg.sender?.last_name ?? ''}`.trim();
                  return (
                    <div
                      key={msg.id}
                      className={`note ${isOwnMessage ? 'note--own' : ''} ${isDeleting ? 'note--deleting' : ''}`.trim()}
                    >
                      <span className="note__avatar" title={senderName} aria-hidden="true">
                        {getInitials(msg.sender?.first_name, msg.sender?.last_name)}
                      </span>
                      <div className="note__body">
                        <div className="note__meta">
                          <span className="note__name">{isOwnMessage ? 'You' : msg.sender?.first_name}</span>
                          <span className="note__time">{formatMessageTime(msg.created_at)}</span>
                          {isOwnMessage && (
                            <span className="row-actions">
                              <IconButton
                                size="sm"
                                tone="danger"
                                icon={<TrashIcon size={14} />}
                                label="Delete note"
                                onClick={() => handleDeleteMessage(msg.id)}
                                disabled={isDeleting}
                              />
                            </span>
                          )}
                        </div>
                        <div className="note__content">{msg.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {error && (
        <p className="thread-panel__error" role="alert">{error}</p>
      )}

      {/* The composer sits in a recessed well, like the editor's insert dock. */}
      <div className="thread-panel__composer">
        <textarea
          ref={textareaRef}
          className="thread-panel__textarea"
          value={newMessage}
          onChange={(e) => {
            setNewMessage(e.target.value);
            autoResizeTextarea();
          }}
          onKeyDown={handleKeyDown}
          placeholder="Add a note…"
          aria-label="New note"
          rows={1}
          disabled={isSending || !currentUserTeamMemberId}
        />
        <button
          type="button"
          onClick={handleSendMessage}
          disabled={isSending || !newMessage.trim() || !currentUserTeamMemberId}
          className="thread-panel__send"
          aria-label={isSending ? 'Sending' : 'Send note'}
          title="Send (Enter)"
        >
          {isSending ? (
            <span className="thread-panel__send-spinner" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 2 11 13" />
              <path d="m22 2-7 20-4-9-9-4 20-7z" />
            </svg>
          )}
        </button>
      </div>
      {confirmDialog}
    </div>
  );
};

export default ThreadPanel;
